import type {
    TApiColumnMappingFieldType,
    TApiColumnMappingResponse,
    TApiDeal,
    TApiDealPropcodeMapping,
    TApiDealSegmentMapping,
} from "@/apis/proda_finance";
import { parseFormulaExpression } from "@/ui/molecules/AmountHeaderFormulaModal";
import type { TFilteringRule } from "@/ui/organisms/OrganismFilteringRules";
import { buildXlsxFile } from "@/utils/downloadXlsx";
import type { TColumnMappingData, TPropcodeMappingRow, TSegmentNumber } from "./constants";
import { PROPCODE_REQUIRED_COLUMNS, SEGMENT_CONFIGS, emptyColumnMappingData } from "./constants";
import type { TDealFormData } from "./steps";

const SEGMENT_NUMBERS: TSegmentNumber[] = [2, 4, 5, 6, 7];

/** Everything read from the source deal, before it is shaped into wizard state. */
export type TDealCopySource = {
    deal: TApiDeal;
    columnMappings: TApiColumnMappingResponse[];
    /** Absent for a customer module whose deals have no segments. */
    segmentMappings?: TApiDealSegmentMapping[];
    propcodeMappings: TApiDealPropcodeMapping[];
    /** The source deal's original OTB upload, or null when there is none to copy. */
    otbFile: File | null;
    /** Why the OTB could not be copied, shown on step 5; null when copied or never present. */
    otbUnavailableReason: string | null;
};

/** Deal fields carried over from the source deal that the create wizard has no step for. */
export type TDealCopyExtraFields = {
    quarterly_deadline_day: number | null;
    year_end_deadline: string | null;
    yardi_entity_id: string;
    yardi_database: string;
    otb_comparison_enabled: boolean;
    accepts_fin_journals: boolean;
    is_test: boolean;
    notes: string;
    import_source_book_names: string[];
};

/** Initial wizard state for a copied deal. Segment / propcode / OTB files are re-validated on mount. */
export type TDealCopyPrefill = {
    sourceDealId: string;
    sourceDealName: string;
    sourceYardiEntityId: string;
    sourceYardiDatabase: string;
    /** Source deal's start period as "YYYY-MM", or null when it has none. */
    sourceStartPeriod: string | null;
    formData: Omit<TDealFormData, "service_provider_id" | "portfolio_company_id">;
    extraFields: TDealCopyExtraFields;
    tbColumnMapping: TColumnMappingData;
    glColumnMapping: TColumnMappingData;
    tbFilteringRules: TFilteringRule[];
    glFilteringRules: TFilteringRule[];
    segmentFiles: Partial<Record<TSegmentNumber, File>>;
    propcodeFile: File | null;
    /**
     * The source rows with their own level. Sent instead of the re-parsed file rows while
     * the copied file is kept: the parser derives level from the target's first letter,
     * which can differ from a level set by hand on the source deal.
     */
    propcodeRows: TPropcodeMappingRow[];
    otbFile: File | null;
    otbUnavailableReason: string | null;
};

/** Default name for the copy; the user can change it on step 1. */
export function copyDealName(name: string): string {
    return `${name} (Copy)`;
}

function toFilteringRules(rules: TApiDeal["tb_filtering_rules"]): TFilteringRule[] {
    return rules.map((r) => ({
        filteringColumnHeader: r.filtering_column_header,
        filteringValue: [...r.filtering_value],
        includeFilteredValues: r.include_filtered_values,
    }));
}

/**
 * Column mappings of one file type as wizard state. There is no sample file to
 * attach (the original upload is not kept), so `columns` comes from the stored
 * header list and `origin` marks the state as copied for the step-2 checks.
 */
export function toCopiedColumnMapping(
    mappings: TApiColumnMappingResponse[],
    fileType: "tb" | "gl",
): TColumnMappingData {
    const ofType = mappings.filter((m) => m.file_type === fileType && m.is_active);
    if (ofType.length === 0) return emptyColumnMappingData;

    const fieldMappings = {} as Record<TApiColumnMappingFieldType, string>;
    for (const m of ofType) fieldMappings[m.field_type] = m.column_name;

    const storedColumns = ofType.find((m) => m.available_columns?.length)?.available_columns;
    // Older deals may predate `available_columns`; fall back to the mapped columns so
    // every mapping still has an option to select.
    const columns = storedColumns?.length
        ? [...storedColumns]
        : Array.from(new Set(ofType.map((m) => m.column_name)));

    return {
        ...emptyColumnMappingData,
        headerRowNumber: ofType[0]!.header_row_number,
        columns,
        mappings: fieldMappings,
        origin: "copied",
    };
}

/** File-name-safe slug of the deal name for generated files. */
function slug(name: string): string {
    return name.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "deal";
}

/** Copied segment mappings as an .xlsx in the exact upload format, one per segment that has rows. */
export function buildSegmentMappingFiles(
    dealName: string,
    mappings: TApiDealSegmentMapping[],
): Partial<Record<TSegmentNumber, File>> {
    const files: Partial<Record<TSegmentNumber, File>> = {};
    for (const segment of SEGMENT_NUMBERS) {
        const rows = mappings.filter((m) => m.segment_type === segment && m.is_active);
        if (rows.length === 0) continue;
        const config = SEGMENT_CONFIGS[segment];
        files[segment] = buildXlsxFile(
            `${slug(dealName)}-segment-${segment}-mappings-copied`,
            config.requiredColumns,
            rows.map((m) => ({
                "Source Counterparty Code": m.source_segment_value,
                "Source Counterparty Name": m.source_segment_name || "",
                [config.targetColumnName]: m.target_segment_value,
            })),
            `Segment ${segment} Mappings`,
        );
    }
    return files;
}

/**
 * Copied propcode mappings as an .xlsx in the exact upload format. `Level` is not a
 * column: the parser derives it from the first character of Platform Entity Code.
 */
export function buildPropcodeMappingFile(
    dealName: string,
    mappings: TApiDealPropcodeMapping[],
): File | null {
    const rows = mappings.filter((m) => m.is_active);
    if (rows.length === 0) return null;
    return buildXlsxFile(
        `${slug(dealName)}-propcode-mappings-copied`,
        PROPCODE_REQUIRED_COLUMNS,
        rows.map((m) => ({
            "Third Entity Code": m.source_property_code,
            "Platform Entity Code": m.target_property_code,
        })),
        "Propcode Mappings",
    );
}

/** "YYYY-MM" key of a start period, for comparing a copy's start with its source's. */
export function startPeriodKey(month: number, year: number): string {
    return `${year}-${String(month).padStart(2, "0")}`;
}

function parseStartPeriod(startPeriod: string | null, fallback: Date): [number, number] {
    const match = startPeriod?.match(/^(\d{4})-(\d{2})/);
    if (!match) return [fallback.getMonth() + 1, fallback.getFullYear()];
    return [Number(match[2]), Number(match[1])];
}

/** Shape a source deal into the initial state of the create wizard. */
export function buildDealCopyPrefill(source: TDealCopySource, now: Date): TDealCopyPrefill {
    const { deal } = source;
    const [startMonth, startYear] = parseStartPeriod(deal.start_period, now);
    const isTbAndGl = deal.deal_file_type === "tb_and_gl";

    return {
        sourceDealId: deal.id,
        sourceDealName: deal.name,
        sourceYardiEntityId: deal.yardi_entity_id ?? "",
        sourceYardiDatabase: deal.yardi_database ?? "",
        sourceStartPeriod: deal.start_period?.match(/^\d{4}-\d{2}/)?.[0] ?? null,
        formData: {
            name: copyDealName(deal.name),
            // Codes are unique per company, so a copy never inherits one.
            code: "",
            finance_user_ids: [...deal.finance_user_ids],
            deal_file_type: deal.deal_file_type,
            // The wizard offers only YTD / MTD; any other GL type is left for the user to pick.
            gl_type:
                isTbAndGl && (deal.gl_type === "ytd" || deal.gl_type === "mtd") ? deal.gl_type : "",
            import_available: deal.import_available,
            start_period_month: startMonth,
            start_period_year: startYear,
            deadline: deal.monthly_deadline_day ?? 2,
            final_destination: deal.final_file_destination,
            segment_2_mapping_type: deal.segment_2_mapping_type,
            segment_4_mapping_type: deal.segment_4_mapping_type,
            segment_5_mapping_type: deal.segment_5_mapping_type,
            segment_6_mapping_type: deal.segment_6_mapping_type,
            segment_7_mapping_type: deal.segment_7_mapping_type,
            accepts_otb: true,
        },
        extraFields: {
            quarterly_deadline_day: deal.quarterly_deadline_day,
            year_end_deadline: deal.year_end_deadline,
            yardi_entity_id: deal.yardi_entity_id ?? "",
            yardi_database: deal.yardi_database ?? "",
            otb_comparison_enabled: deal.otb_comparison_enabled,
            accepts_fin_journals: deal.accepts_fin_journals,
            is_test: deal.is_test,
            notes: deal.notes ?? "",
            import_source_book_names: deal.import_source_books.map((b) => b.name),
        },
        tbColumnMapping: toCopiedColumnMapping(source.columnMappings, "tb"),
        glColumnMapping: isTbAndGl
            ? toCopiedColumnMapping(source.columnMappings, "gl")
            : emptyColumnMappingData,
        tbFilteringRules: toFilteringRules(deal.tb_filtering_rules),
        glFilteringRules: isTbAndGl ? toFilteringRules(deal.gl_filtering_rules) : [],
        segmentFiles: buildSegmentMappingFiles(deal.name, source.segmentMappings ?? []),
        propcodeFile: buildPropcodeMappingFile(deal.name, source.propcodeMappings),
        propcodeRows: source.propcodeMappings
            .filter((m) => m.is_active)
            .map((m) => ({
                level: m.level,
                source_propcode: m.source_property_code,
                target_propcode: m.target_property_code,
            })),
        otbFile: source.otbFile,
        otbUnavailableReason: source.otbUnavailableReason,
    };
}

/**
 * When a newly uploaded sample replaces a copied one, keep each mapping whose
 * column still exists and report the ones that were dropped. An Amount formula
 * ("Debit - Credit") is kept when both of its columns exist.
 */
export function reconcileMappingsWithColumns(
    mappings: Record<TApiColumnMappingFieldType, string>,
    columns: string[],
): { mappings: Record<TApiColumnMappingFieldType, string>; droppedColumns: string[] } {
    const available = new Set(columns);
    const kept = {} as Record<TApiColumnMappingFieldType, string>;
    const droppedColumns: string[] = [];
    for (const [field, column] of Object.entries(mappings) as [
        TApiColumnMappingFieldType,
        string,
    ][]) {
        if (!column?.trim()) continue;
        const formula = parseFormulaExpression(column);
        const exists = formula
            ? available.has(formula.left) && available.has(formula.right)
            : available.has(column);
        if (exists) kept[field] = column;
        else droppedColumns.push(column);
    }
    return { mappings: kept, droppedColumns };
}

/**
 * When a newly uploaded sample replaces copied columns, drop each filtering rule whose
 * column the new file lacks. Upload processing rejects a file that is missing a filtered
 * column, so a stale rule would block every future upload. Matching is case-insensitive,
 * as in processing.
 */
export function reconcileFilteringRulesWithColumns(
    rules: TFilteringRule[],
    columns: string[],
): { rules: TFilteringRule[]; droppedColumns: string[] } {
    const available = new Set(columns.map((c) => c.toLowerCase()));
    const kept: TFilteringRule[] = [];
    const droppedColumns: string[] = [];
    for (const rule of rules) {
        const header = rule.filteringColumnHeader.trim();
        if (!header || available.has(header.toLowerCase())) kept.push(rule);
        else droppedColumns.push(header);
    }
    return { rules: kept, droppedColumns };
}
